import React, { useState, useEffect, useRef } from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select } from './ui/select';
import { ConsentCheckbox } from './ui/ConsentCheckbox';
import { browserControlWS } from '../services/browserControlWebSocket';
import { carTitle } from '../utils/carTitle';
import './TestDriveModal.css';
import { useTranslation } from '../i18n/I18nContext';
import { buildLeadConsents, useConsentLocale } from '../utils/leadConsent';
import { config } from '../config/runtimeConfig';

const TestDriveModal = ({ isOpen, onClose, selectedCar, cars }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    message: '',
    selectedCarId: selectedCar?.id || '',
    // #1440 — the two consent boxes: required for this request, optional for offers.
    requestConsent: false,
    marketingConsent: false
  });
  const consentLocale = useConsentLocale();

  // Отслеживать открытие/закрытие модального окна
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      // Modal открылся
      // Пропустить, если открытие инициировано агентом
      if (!window.__agentCommandInProgress) {
        browserControlWS.sendUserAction('modal-opened', {
          modalType: 'test-drive',
          carId: selectedCar?.id,
          // year is optional (#1206) — filter so we never send "undefined Toyota Yaris"
          carInfo: selectedCar ? carTitle(selectedCar) : null,
        }, false);
      } else {
        console.log('[TestDriveModal] Skipping modal-opened event (agent command in progress)');
      }
      wasOpenRef.current = true;
    } else if (!isOpen && wasOpenRef.current) {
      // Modal закрылся
      // Пропустить, если закрытие инициировано агентом
      if (!window.__agentCommandInProgress) {
        browserControlWS.sendUserAction('modal-closed', {
          modalType: 'test-drive',
        }, false);
      } else {
        console.log('[TestDriveModal] Skipping modal-closed event (agent command in progress)');
      }
      wasOpenRef.current = false;
    }
  }, [isOpen, selectedCar]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to your backend.
    // TODO(#1440): still a stub — when this submits to /api/widget/lead-capture
    // via submitLeadFromForm, send `consents` exactly as built here.
    console.log('Test drive booking:', {
      ...formData,
      consents: buildLeadConsents(
        { request: formData.requestConsent, marketing: formData.marketingConsent },
        consentLocale
      ),
    });
    alert('Thank you! We will contact you soon to confirm your test drive appointment.');
    onClose();
    setFormData({
      name: '',
      email: '',
      phone: '',
      preferredDate: '',
      message: '',
      selectedCarId: selectedCar?.id || '',
      requestConsent: false,
      marketingConsent: false
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="test-drive-modal">
      <div className="test-drive-modal__panel">
        <div className="test-drive-modal__header">
          <h2 className="test-drive-modal__title">{t('showcase.testDrive.title', 'Book your test drive')}</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="test-drive-modal__body">
          <div>
            <Label htmlFor="name">{t('showcase.testDrive.name', 'Name')} *</Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
              className="test-drive-modal__note"
            />
          </div>

          <div className="test-drive-modal__pair">
            <div>
              <Label htmlFor="email">{t('showcase.testDrive.email', 'Email')}</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="test-drive-modal__note"
              />
            </div>
            <div>
              <Label htmlFor="phone">{t('showcase.testDrive.phone', 'Phone')} *</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
                className="test-drive-modal__note"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="preferredDate">{t('showcase.testDrive.date', 'Preferred date and time')}</Label>
            <Input
              id="preferredDate"
              type="datetime-local"
              value={formData.preferredDate}
              onChange={(e) => handleInputChange('preferredDate', e.target.value)}
              className="test-drive-modal__note"
            />
          </div>

          <div>
            <Label htmlFor="message">{t('showcase.testDrive.message', 'Message')}</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder={t('showcase.testDrive.messagePlaceholder', 'Any specific questions or requirements?')}
              className="test-drive-modal__note"
            />
          </div>

          <div>
            <Label htmlFor="car-select">{t('showcase.testDrive.car', 'Select a car')}</Label>
            <Select
              id="car-select"
              value={formData.selectedCarId}
              onChange={(e) => handleInputChange('selectedCarId', parseInt(e.target.value))}
              className="test-drive-modal__note"
            >
              <option value="">{t('showcase.testDrive.carPlaceholder', 'Select a car')}</option>
              {cars.map((car) => (
                <option key={car.id} value={car.id}>
                  {carTitle(car)} - €{car.price.toLocaleString()}
                </option>
              ))}
            </Select>
          </div>

          {/* Stub modal, mounted without ContactParamsProvider in Storybook —
              no dealer name here, the text says "the dealer". The request box
              is required: the browser won't submit without it. */}
          <ConsentCheckbox
            box="request"
            id="test-drive-consent-request"
            checked={formData.requestConsent}
            onCheckedChange={(checked) => handleInputChange('requestConsent', checked)}
            dealerName={null}
            privacyUrl={config.DEALER_PRIVACY_URL}
            locale={consentLocale}
          />
          <ConsentCheckbox
            box="marketing"
            id="test-drive-consent-marketing"
            checked={formData.marketingConsent}
            onCheckedChange={(checked) => handleInputChange('marketingConsent', checked)}
            dealerName={null}
            locale={consentLocale}
          />

          <Button
            type="submit"
            className="test-drive-modal__submit"
            disabled={!formData.name || !formData.phone}
          >
            Schedule Test Drive
          </Button>
        </form>
      </div>
    </div>
  );
};

export default TestDriveModal;

